import React, { useState, useEffect, useCallback } from 'react';
import { Zap, TrendingUp, Package, Loader2, ExternalLink, AlertCircle, RefreshCw, Mail, MessageCircle } from 'lucide-react';
import { CONTATO_PADRAO } from '@/components/AcessoDoCliente';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { useVoltaDoFoco } from '@/lib/voltaDoFoco';
import axios from 'axios';
import { toast } from 'sonner';
import { mensagemDoErro } from '@/lib/mensagemDoErro';
import { formatarMoeda, formatarCreditos } from '@/lib/formatarNumero';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLAN_LABELS = {
  cove:    'Cove',
  harbor:  'Harbor',
  voyage:  'Voyage',
  admiral: 'Admiral',
};

/* A opção do DS (`.ark-item` como botão): fio do tema, realce em muted, foco com
 * anel. Sem verde como texto e sem cor por plano. */
const OPCAO = 'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-border text-left '
  + 'transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring '
  + 'disabled:opacity-50 disabled:cursor-not-allowed';

export function UpsellModal() {
  const [open, setOpen]             = useState(false);
  const [detail, setDetail]         = useState(null);
  const [tab, setTab]               = useState('addon'); // 'addon' | 'upgrade'
  const [plans, setPlans]           = useState(null);   // { planos, pacotes_avulsos }
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [erroPlanos, setErroPlanos] = useState(false);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const voltaDoFoco = useVoltaDoFoco();

  // O MODAL SEMPRE FECHA (X, Esc e clique fora), inclusive com `brand_id`, por decisão
  // do fundador de 22/10 (DS-3b). Antes ele só fechava sem `brand_id`, e nos fluxos
  // reais (402, faixa e aviso de 80%) a única saída era assinar ou recarregar.
  // Fechar não perde nada: o 402 do backend recusa ANTES de debitar
  // (billing_utils.py), o interceptor do App.js continua rejeitando a promessa para a
  // ação paga mostrar a mensagem dela, e a próxima tentativa paga dispara o evento de
  // novo, que reabre este modal.

  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    setErroPlanos(false);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/billing/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // SEM PRECO DE RESERVA NO CODIGO. Se a lista nao vier, o modal mostra o estado
      // de erro do DS com "tentar de novo": preco escrito aqui podia nao ser o vigente.
      const ok = res.data && Array.isArray(res.data.pacotes_avulsos)
        && res.data.planos && typeof res.data.planos === 'object';
      if (ok) setPlans(res.data);
      else { setPlans(null); setErroPlanos(true); }
    } catch {
      setPlans(null);
      setErroPlanos(true);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setDetail(e.detail || {});
      setOpen(true);
      setTab('addon');
      fetchPlans();
    };
    window.addEventListener('saldo_insuficiente', handler);
    return () => window.removeEventListener('saldo_insuficiente', handler);
  }, [fetchPlans]);

  const handleUpgradePlan = async (planKey) => {
    if (!detail?.brand_id) {
      toast.error('Selecione uma marca antes de fazer upgrade.');
      return;
    }
    setCheckoutLoadingPlan(planKey);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/billing/checkout`,
        { plan: planKey, brand_id: detail.brand_id, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Erro ao criar sessão de pagamento.');
      }
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Erro ao iniciar checkout.'));
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/billing/portal`, {
        params: { origin_url: window.location.origin },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.portal_url) {
        window.open(res.data.portal_url, '_blank');
      }
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Sem assinatura ativa para gerenciar.'));
    } finally {
      setPortalLoading(false);
    }
  };

  const necessarios  = detail?.creditos_necessarios ?? '?';
  const disponiveis  = detail?.creditos_disponiveis ?? 0;

  const addonPackages = plans?.pacotes_avulsos ?? [];

  const planUpgrades = plans?.planos
    ? Object.entries(plans.planos)
        .filter(([key]) => key !== 'cove')
        .map(([key, info]) => ({ plano: key, label: PLAN_LABELS[key] || key, credits: info.creditos, price: info.preco }))
    : [];

  // Carregando e erro sao os mesmos nas duas abas (README do DS, secao 13).
  const carregando = (
    <div className="space-y-2" data-testid="upsell-carregando">
      {[0, 1, 2].map((n) => <Skeleton key={n} className="h-14 w-full rounded-2xl" />)}
    </div>
  );
  const erro = (
    <Alert variant="destructive" data-testid="upsell-erro-planos">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Não foi possível carregar os planos</AlertTitle>
      <AlertDescription>
        <p>Verifique a conexão e tente de novo.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={fetchPlans} data-testid="upsell-tentar-de-novo">
          <RefreshCw className="h-3.5 w-3.5" />
          Tentar de novo
        </Button>
      </AlertDescription>
    </Alert>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md gap-5 p-6" data-testid="upsell-modal" {...voltaDoFoco}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-destructive" />
            Créditos insuficientes
          </DialogTitle>
          <DialogDescription>
            Necessário: <span className="font-mono tabular-nums text-destructive">{formatarCreditos(necessarios)}</span>
            {' · '}
            Disponível: <span className="font-mono tabular-nums text-foreground">{formatarCreditos(disponiveis)}</span>
          </DialogDescription>
        </DialogHeader>

        {/* LOTE 204 (C2): quem paga por transferência ou tem cortesia nunca vê o Stripe: fala com a Arkipel. */}
        {detail?.fale_com_a_arkipel ? (
          <div className="space-y-3" data-testid="upsell-fale-com-a-arkipel">
            {detail?.acao && <p className="text-sm text-muted-foreground">Ação: {detail.acao}</p>}
            <p className="text-sm text-foreground">Fale com a Arkipel para recarregar os créditos da sua marca.</p>
            <Button asChild className="w-full">
              <a href={`mailto:${detail.contato || CONTATO_PADRAO}`} data-testid="upsell-contato">
                <Mail className="h-4 w-4" aria-hidden="true" />{detail.contato || CONTATO_PADRAO}
              </a>
            </Button>
            {detail?.whatsapp_link && (
              <Button asChild variant="outline" className="w-full">
                <a href={detail.whatsapp_link} target="_blank" rel="noreferrer" data-testid="upsell-whatsapp">
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />WhatsApp da Arkipel
                </a>
              </Button>
            )}
          </div>
        ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="addon" className="flex-1 gap-1.5" data-testid="tab-addon">
              <Package className="h-3.5 w-3.5" />
              Créditos Avulsos
            </TabsTrigger>
            <TabsTrigger value="upgrade" className="flex-1 gap-1.5" data-testid="tab-upgrade">
              <TrendingUp className="h-3.5 w-3.5" />
              Upgrade de Plano
            </TabsTrigger>
          </TabsList>

          <TabsContent value="addon" className="mt-4 space-y-2">
            {loadingPlans ? carregando : erroPlanos ? erro : (
              <>
                <p className="text-xs text-muted-foreground mb-3">
                  Créditos avulsos não expiram por 12 meses.
                </p>
                {addonPackages.map((pkg, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      toast.info('Créditos avulsos em breve! Use upgrade de plano para acesso agora.');
                    }}
                    className={OPCAO}
                    data-testid={`addon-pkg-${pkg.creditos}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                        <Zap className="h-4 w-4 text-brand-ink" />
                      </span>
                      <span className="text-sm font-medium">{formatarCreditos(pkg.creditos)}</span>
                    </span>
                    <span className="text-sm font-mono tabular-nums">{formatarMoeda(pkg.preco)}</span>
                  </button>
                ))}
              </>
            )}
          </TabsContent>

          <TabsContent value="upgrade" className="mt-4 space-y-2">
            {loadingPlans ? carregando : erroPlanos ? erro : (
              <>
                <p className="text-xs text-muted-foreground mb-3">
                  Créditos mensais renovam automaticamente todo mês.
                </p>
                {planUpgrades.map((plan) => {
                  const isAdmiral = plan.plano === 'admiral';
                  const isLoading = checkoutLoadingPlan === plan.plano;
                  return (
                    <button
                      key={plan.plano}
                      onClick={() => isAdmiral ? null : handleUpgradePlan(plan.plano)}
                      disabled={isLoading || isAdmiral}
                      className={OPCAO}
                      data-testid={`upgrade-plan-${plan.plano}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          {isLoading
                            ? <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                            : <TrendingUp className="h-4 w-4 text-muted-foreground" />}
                        </span>
                        <span className="text-left">
                          <span className="block text-sm font-medium">
                            {isLoading ? 'Redirecionando...' : `Assinar ${plan.label}`}
                          </span>
                          <span className="block text-xs text-muted-foreground">{formatarCreditos(plan.credits)}/mês</span>
                        </span>
                      </span>
                      <span className="text-sm font-mono tabular-nums flex-shrink-0">
                        {plan.price > 0 ? `R$ ${plan.price.toLocaleString('pt-BR')}/mês` : 'Sob consulta'}
                      </span>
                    </button>
                  );
                })}

                <div className="pt-2 border-t border-border mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="w-full"
                    data-testid="manage-subscription-btn"
                  >
                    {portalLoading
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <ExternalLink className="h-3.5 w-3.5" />}
                    Gerenciar assinatura existente
                  </Button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
        )}

        <DialogFooter>
          <Button
            onClick={() => setOpen(false)}
            variant="outline"
            className="w-full"
            data-testid="upsell-dismiss"
          >
            Continuar sem gerar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
